import React, { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Mountain, MapPin, HandHeart, Sprout, Droplets, Package, UtensilsCrossed } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const STATIONS = [
  { icon: Mountain, ar: ["الأرض", "كروم وتلال فلسطين، حيث تبدأ كل حكاية."], en: ["The Land", "Palestine's hills and groves, where every story begins."] },
  { icon: MapPin, ar: ["المنطقة", "جنين، نابلس، الخليل، أريحا... لكل منطقة نكهتها وطبعها."], en: ["The Region", "Jenin, Nablus, Hebron, Jericho — each with its own flavor."] },
  { icon: HandHeart, ar: ["المصدر", "مزارعون وعائلات نعرفها بالاسم ونثق بمنتجها."], en: ["The Source", "Farmers and families we know by name and trust."] },
  { icon: Sprout, ar: ["المكونات", "مكوّن واحد صادق من الأرض، بلا إضافات."], en: ["The Ingredients", "One honest ingredient from the land, nothing added."] },
  { icon: Droplets, ar: ["طريقة الإنتاج", "عصر على البارد، تجفيف بطيء، وحفظ بعناية."], en: ["The Making", "Cold-pressed, slow-dried, carefully kept."] },
  { icon: Package, ar: ["المنتج", "يُغلَّف ويُوثَّق ببصمة الأرض قبل أن يغادر."], en: ["The Product", "Packed and stamped with the Land Fingerprint."] },
  { icon: UtensilsCrossed, ar: ["المائدة", "يصل إلى مائدتك كما خرج من أرضه تماماً."], en: ["Your Table", "Arrives at your table just as it left its land."] },
];

export default function JourneySection() {
  const { lang, t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.8", "end 0.65"] });
  const scaleY = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

  return (
    <section data-testid="journey-section" className="relative overflow-hidden bg-olive-deep py-24 text-ivory">
      <div className="tatreez-bg absolute inset-0 opacity-15" aria-hidden="true" />
      <div className="relative mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-[1fr_1.15fr]">
        <div className="self-start lg:sticky lg:top-28">
          <Reveal>
            <span className="mb-3 inline-block rounded-full bg-ivory/10 px-4 py-1.5 text-xs font-semibold text-earth">{t("journey_eyebrow")}</span>
            <h2 className="font-display text-3xl font-bold leading-snug sm:text-4xl">
              {t("journey_title1")}<span className="text-earth">{t("journey_title2")}</span>
            </h2>
            <p className="mt-4 max-w-md leading-relaxed text-ivory/70">{t("journey_desc")}</p>
          </Reveal>
        </div>

        <div ref={ref} className="relative">
          <div className="absolute inset-y-2 start-6 w-px bg-ivory/15" aria-hidden="true" />
          <motion.div className="absolute inset-y-2 start-6 w-px origin-top bg-earth" style={{ scaleY }} aria-hidden="true" />
          <div className="space-y-8">
            {STATIONS.map((s, i) => (
              <Reveal key={i} delay={i * 40}>
                <div className="flex items-start gap-5 ps-0">
                  <span className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-earth/40 bg-olive-deep shadow-lg">
                    <s.icon className="h-5 w-5 text-earth" />
                  </span>
                  <div className="rounded-2xl border border-ivory/10 bg-ivory/[0.04] p-5 backdrop-blur-sm">
                    <h3 className="font-display text-lg font-semibold text-ivory">{s[lang === "en" ? "en" : "ar"][0]}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-ivory/65">{s[lang === "en" ? "en" : "ar"][1]}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
